import React, { Dispatch, useState, useImperativeHandle, forwardRef } from 'react';
import { Form, DatePicker, Radio, Modal, Tooltip } from 'antd';
import moment, { Moment } from 'moment';
import { Action } from 'typesafe-actions';
import { CloseOutlined, ReadOutlined } from '@ant-design/icons';
import { ModalContainer, Plataforma } from './styles';
import { CALENDAR_LOCALE } from '../../../../utils/calendar-locale';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import ModalConfirmaAgendamento from '../ModalConfirmaAgendamento';
import Spin from '../../../../components/Spin';
import { ActionButton } from '../../../../components/_REFACTORED/ActionButton';
import TitlePage from 'components/TitlePage';
import {
  loadRequest as loadHorariosDisponiveis,
  cleanHorariosDisponiveis,
} from '../../../../store/ducks/horariosDisponiveis/actions';
import { cleanOrdemCarga } from '../../../../store/ducks/ordemCarga/actions';

import { IOrdemCarga } from '../../../../store/ducks/ordemCarga/types';
import Swal from 'sweetalert2';
import { TIPO_WMS } from 'utils/enums/tipoWms';

interface ModalCriarAgendamentoProps {
  ordemCarga?: IOrdemCarga;
  status?: string;
  setIsModalOrdemCargaVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  atualizaTabelaEntrega?: () => void;
  atualizaTabelaColeta?: () => void;
  atualizaTabelaOrdemCarga?: () => void;
}

export interface ModalCriarAgendamentoRef {
  showModal: () => void;
}

const ModalCriarAgendamento: React.ForwardRefRenderFunction<
  ModalCriarAgendamentoRef,
  ModalCriarAgendamentoProps
> = (
  {
    ordemCarga,
    status,
    setIsModalOrdemCargaVisible,
    atualizaTabelaColeta,
    atualizaTabelaEntrega,
    atualizaTabelaOrdemCarga,
  },
  ref
) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch<Dispatch<Action>>();
    const contato = useSelector((state: ApplicationState) => state.auth.cddAtivo?.contato);
    const cddAtivo = useSelector((state: ApplicationState) => state.auth.cddAtivo);

    const loadingHorariosDisponiveis = useSelector(
      (state: ApplicationState) => state.HorariosDisponiveis.loading
    );
    const horariosDisponiveis = useSelector(
      (state: ApplicationState) => state.HorariosDisponiveis.data
    );
    const user = useSelector((state: ApplicationState) => state.auth.user);
    const [isModalAgendamentoVisible, setIsModalAgendamentoVisible] = useState(false);
    const [hora, setHora] = useState<string | undefined>();

    const getHorario = (value: string) => {
      const data = value?.split(' ');
      return data[1];
    };

    const onReset = () => {
      form.resetFields();
      setHora(undefined);
      dispatch(cleanHorariosDisponiveis());
    };

    const handleChangeDate = (date: moment.Moment | string | null) => {
      const dateIsTomorrow =
        moment(date).format('DD/MM/YYYY') === moment().add(1, 'days').format('DD/MM/YYYY');
      const houerIsBiggerThen20 = moment().format('HH:mm') > moment().format('20:00');
      const houerIsSmallerThen23 = moment().format('HH:mm') < moment().format('23:59');
      const isColeta = ordemCarga?.tipoWms === TIPO_WMS.coleta;

      if (!date) {
        return onReset();
      }
      dispatch(
        loadHorariosDisponiveis(
          moment(date).format(),
          Number(ordemCarga?.codTipVeiculo),
          Number(ordemCarga?.codCdd),
          Number(ordemCarga?.codEmp),
          Number(ordemCarga?.ordemCarga)
        )
      );

      if (dateIsTomorrow && houerIsBiggerThen20 && houerIsSmallerThen23 && isColeta) {
        Swal.fire({
          icon: 'info',
          title: 'Aviso',
          text:
            'Para agendar uma coleta para o dia seguinte, sendo o horário atual das 20:00 até 23:59, o primeiro horário disponível para agendamento será daqui a 16 horas.',
          showCancelButton: true,
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancelar',
        }).then((result) => {
          if (result.isDismissed) {
            onReset();
          }
        });
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const showModal = () => {
      setIsModalAgendamentoVisible(true);
    };

    useImperativeHandle(
      ref,
      () => ({
        showModal,
      }),
      [showModal]
    );

    return (
      <>
        <Modal
          centered
          title={<TitlePage>Agendamento da ordem de carga {ordemCarga?.ordemCarga}</TitlePage>}
          visible={isModalAgendamentoVisible}
          onCancel={() => {
            setIsModalOrdemCargaVisible && setIsModalOrdemCargaVisible(false);
            setIsModalAgendamentoVisible(false);
            atualizaTabelaEntrega && atualizaTabelaEntrega();
            atualizaTabelaColeta && atualizaTabelaColeta();
            atualizaTabelaOrdemCarga && atualizaTabelaOrdemCarga();
            dispatch(cleanHorariosDisponiveis());
            dispatch(cleanOrdemCarga());
            onReset();
          }}
          maskClosable={false}
          closable={true}
          destroyOnClose={true}
          closeIcon={<CloseOutlined />}
          keyboard={true}
          width={911}
          footer={[
            <div onClick={() => setIsModalOrdemCargaVisible && setIsModalOrdemCargaVisible(false)}>
              {hora && (
                <ModalConfirmaAgendamento
                  ordemCarga={ordemCarga}
                  setIsModalOrderVisible={setIsModalAgendamentoVisible}
                  horarioSelecionado={hora}
                  atualizaTabelaColeta={atualizaTabelaColeta}
                  atualizaTabelaEntrega={atualizaTabelaEntrega}
                  atualizaTabelaOrdemCarga={atualizaTabelaOrdemCarga}
                />
              )}
            </div>,
          ]}
        >
          <ModalContainer>
            <h3>Selecione a data</h3>
            <DatePicker
              data-cy="datePicker"
              disabledDate={(e) => {
                if (user?.masterIncluir || cddAtivo?.incluiAgendamentoNoDia) {
                  return e < moment().startOf('day');
                }
                return e < moment().endOf('day');
              }}
              onChange={(date) => handleChangeDate(date as Moment | null )}
              locale={CALENDAR_LOCALE}
              format="DD/MM/YYYY"
            />

            {loadingHorariosDisponiveis ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100px',
                }}
              >
                <Spin color="#1890FF" />
              </div>
            ) : horariosDisponiveis === undefined ? (
              <Plataforma>Selecione uma data</Plataforma>
            ) : horariosDisponiveis && horariosDisponiveis?.length > 0 ? (
              <>
                <Plataforma>Horários disponíveis</Plataforma>
                <Radio.Group>
                  {horariosDisponiveis?.map((item: any) => {
                    return (
                      <Radio.Button
                        key={getHorario(item?.dhAgendamento)}
                        onClick={() => setHora(item?.dhAgendamento)}
                        value={getHorario(item?.dhAgendamento)}
                      >
                        {getHorario(item?.dhAgendamento)}
                      </Radio.Button>
                    );
                  })}
                </Radio.Group>
              </>
            ) : (
              <Plataforma>Não há horários disponíveis</Plataforma>
            )}

            <span className="info">
              <p>
                <strong>Esses horários não te atendem?</strong>
              </p>
              <p>
                Ligue para <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{contato}</span>{' '}
                para agendar uma nova data ou horário.
              </p>
            </span>
          </ModalContainer>
        </Modal>

        <Tooltip
          placement="top"
          title={status !== 'AGA' && 'Não é possível fazer o agendamento devido ao seu status'}
        >
          <ActionButton
            disabled={status !== 'AGA'}
            onClick={() => showModal()}
            icon={<ReadOutlined />}
          >
            Agendar ordem de carga
          </ActionButton>
        </Tooltip>
      </>
    );
  };

export default forwardRef(ModalCriarAgendamento);
